import { REFRESH_TOKEN_STORAGE_KEY } from "config/constants";
import keycloak, { KEYCLOAK_SCOPE } from "keycloak";

export interface AxiosLikeResponse<T = any> {
    data: T;
    status: number;
    statusText: string;
    headers: Headers;
    config: RequestInit & { url: string };
    request: Request;
}

export interface AxiosLikeError<T = any> extends Error {
    isAxiosError: true;
    config: RequestInit & { url: string };
    code?: string;
    request?: Request;
    response?: AxiosLikeResponse<T>;
}

async function parseBody<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
        try {
            return (await response.json()) as T;
        } catch {
            // empty body or invalid JSON – fall through
        }
    }

    if (contentType.startsWith("text/")) {
        return ((await response.text()) as unknown) as T;
    }

    return (null as unknown) as T;
}

export async function axiosLikeFetch<T = any>(
    url: string,
    options: RequestInit = {}
): Promise<AxiosLikeResponse<T>> {
    const request = new Request(url, options);
    const config: RequestInit & { url: string } = { url, ...options };

    let response: Response;

    try {
        response = await fetch(request);
    } catch (err) {
        const message = err instanceof Error ? err.message : "Network Error";
        const error: AxiosLikeError = new Error(message) as AxiosLikeError;
        error.isAxiosError = true;
        error.config = config;
        error.code = "ERR_NETWORK";
        error.request = request;
        throw error;
    }

    const responseData = await parseBody<T>(response);

    const axiosLikeResponse: AxiosLikeResponse<T> = {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        config,
        request,
    };

    if (!response.ok) {
        const error: AxiosLikeError<T> = new Error(
            `Request failed with status code ${response.status}`
        ) as AxiosLikeError<T>;
        error.isAxiosError = true;
        error.config = config;
        error.code = `ERR_HTTP_${response.status}`;
        error.request = request;
        error.response = axiosLikeResponse;
        throw error;
    }

    return axiosLikeResponse;
}

export const getErrorResponse = (err: any) => {
    const errorMessage = err?.response?.data?.ResponseStatus?.Message
        ? err?.response?.data?.ResponseStatus?.Message
        : err.message;
    return errorMessage;
};

export async function clearSession(clientId: string, refreshToken: string) {
    var myHeaders = new Headers();
    var urlencoded = new URLSearchParams();
    myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

    urlencoded.append("refresh_token", refreshToken);
    urlencoded.append("client_id", clientId);

    var requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: urlencoded,
    };

    return fetch(
        `${process.env.REACT_APP_KEYCLOAK_AUTH_URL}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/logout`,
        requestOptions
    );
}
export interface KeycloakResponse {
    access_token: string;
    expires_in: number;
    refresh_expires_in: number;
    refresh_token: string;
    token_type: string;
    id_token: string;
    "not-before-policy": number;
    session_state: string;
    scope: string;
}

type GetTokensArgs = string | null | undefined;

export async function getTokens(refreshToken: GetTokensArgs) {
    try {
        if (!refreshToken) {
            throw new Error(
                `No refresh token passed. Please make sure to pass the refresh token. Value is ${refreshToken}`
            );
        }

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

        var urlencoded = new URLSearchParams();
        urlencoded.append("refresh_token", refreshToken);
        urlencoded.append("grant_type", "refresh_token");
        urlencoded.append(
            "client_id",
            process.env.REACT_APP_KEYCLOAK_CLIENT_ID as string
        );
        urlencoded.append("scope", KEYCLOAK_SCOPE);

        var requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: urlencoded,
            // redirect: "follow",
        };

        return fetch(
            `${process.env.REACT_APP_KEYCLOAK_AUTH_URL}/realms/bookmore-admin/protocol/openid-connect/token`,
            requestOptions
        ).then(async (response: any) => {
            const json = await response.json();
            return json as KeycloakResponse;
        });
    } catch (err) {
        throw err;
    }
}

export const getPersistedOfflineToken = () => {
    const offlineToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    return offlineToken;
};

export const persistOfflineToken = (refreshToken: string) => {
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    }
};

export const clearPersistedOfflineToken = () => {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
};

export function generateInitialState<T>(
    entity?: T
): BokaMera.InitialSliceState<T> {
    return {
        entity: entity || null,
        isLoading: false,
        isError: false,
        isSuccess: false,
        isInitialized: false,
        error: undefined,
    };
}
